/**
 * Lesson Cycle slice
 *
 * Backs the lesson-cycle stickiness model: the Lesson Cycle card (student),
 * the tutor cycle board, cycle-task completion, question capture, and bulk task
 * creation. Talks to /api/lesson-cycle (see server/routes/lesson_cycle.routes.js).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
const initialState = {
    current: null,
    currentStatus: 'idle',
    currentError: null,
    roster: [],
    rosterStatus: 'idle',
    rosterError: null,
    mutating: false,
};
function errMessage(e, fallback) {
    if (typeof e === 'object' && e !== null) {
        const anyErr = e;
        return anyErr.response?.data?.message || anyErr.message || fallback;
    }
    return fallback;
}
export const fetchCurrentCycle = createAsyncThunk('lessonCycle/fetchCurrent', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/lesson-cycle/current');
        return (response.data?.data ?? response.data);
    }
    catch (e) {
        return rejectWithValue(errMessage(e, 'Failed to load current cycle'));
    }
});
export const fetchTutorRoster = createAsyncThunk('lessonCycle/fetchTutorRoster', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/lesson-cycle/tutor-roster');
        return (response.data?.data ?? response.data ?? []);
    }
    catch (e) {
        return rejectWithValue(errMessage(e, 'Failed to load cycle roster'));
    }
});
export const createCycleTasks = createAsyncThunk('lessonCycle/createTasks', async (tasks, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/lesson-cycle/tasks', { tasks });
        return (response.data?.data ?? response.data);
    }
    catch (e) {
        return rejectWithValue(errMessage(e, 'Failed to create cycle tasks'));
    }
});
export const completeCycleTask = createAsyncThunk('lessonCycle/completeTask', async ({ taskId, status = 'completed' }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(`/lesson-cycle/tasks/${taskId}`, { status });
        return (response.data?.data ?? response.data);
    }
    catch (e) {
        return rejectWithValue(errMessage(e, 'Failed to update task'));
    }
});
export const captureQuestion = createAsyncThunk('lessonCycle/captureQuestion', async (input, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/lesson-cycle/questions', {
            text: input.text,
            tutorId: input.tutorId,
            appointmentId: input.appointmentId,
            imageUrl: input.imageUrl,
        });
        return (response.data?.data ?? response.data);
    }
    catch (e) {
        return rejectWithValue(errMessage(e, 'Failed to submit question'));
    }
});
/** Drop a task from whichever active list holds it (used after complete/dismiss). */
function removeTaskFromCurrent(current, taskId, becameCompleted) {
    if (!current)
        return;
    const inPrep = current.prepTasks.some((t) => t.id === taskId);
    current.prepTasks = current.prepTasks.filter((t) => t.id !== taskId);
    current.warmTasks = current.warmTasks.filter((t) => t.id !== taskId);
    current.recapTasks = current.recapTasks.filter((t) => t.id !== taskId);
    if (inPrep && becameCompleted) {
        current.prepCompletion = {
            done: current.prepCompletion.done + 1,
            total: current.prepCompletion.total,
        };
    }
}
const lessonCycleSlice = createSlice({
    name: 'lessonCycle',
    initialState,
    reducers: {
        clearLessonCycle(state) {
            state.current = null;
            state.currentStatus = 'idle';
            state.roster = [];
            state.rosterStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCurrentCycle.pending, (state) => {
            state.currentStatus = 'loading';
            state.currentError = null;
        })
            .addCase(fetchCurrentCycle.fulfilled, (state, action) => {
            state.currentStatus = 'succeeded';
            state.current = action.payload;
        })
            .addCase(fetchCurrentCycle.rejected, (state, action) => {
            state.currentStatus = 'failed';
            state.currentError = action.payload ?? 'Failed to load current cycle';
        })
            .addCase(fetchTutorRoster.pending, (state) => {
            state.rosterStatus = 'loading';
            state.rosterError = null;
        })
            .addCase(fetchTutorRoster.fulfilled, (state, action) => {
            state.rosterStatus = 'succeeded';
            state.roster = action.payload;
        })
            .addCase(fetchTutorRoster.rejected, (state, action) => {
            state.rosterStatus = 'failed';
            state.rosterError = action.payload ?? 'Failed to load cycle roster';
        })
            .addCase(completeCycleTask.pending, (state) => {
            state.mutating = true;
        })
            .addCase(completeCycleTask.fulfilled, (state, action) => {
            state.mutating = false;
            const task = action.payload;
            const isResolved = task.status === 'completed' || task.status === 'dismissed';
            if (isResolved) {
                removeTaskFromCurrent(state.current, task.id, task.status === 'completed');
            }
        })
            .addCase(completeCycleTask.rejected, (state) => {
            state.mutating = false;
        })
            .addCase(createCycleTasks.pending, (state) => {
            state.mutating = true;
        })
            .addCase(createCycleTasks.fulfilled, (state) => {
            state.mutating = false;
        })
            .addCase(createCycleTasks.rejected, (state) => {
            state.mutating = false;
        })
            .addCase(captureQuestion.pending, (state) => {
            state.mutating = true;
        })
            .addCase(captureQuestion.fulfilled, (state) => {
            state.mutating = false;
        })
            .addCase(captureQuestion.rejected, (state) => {
            state.mutating = false;
        });
    },
});
export const { clearLessonCycle } = lessonCycleSlice.actions;
// Selectors
export const selectCurrentCycle = (state) => state.lessonCycle.current;
export const selectCurrentCycleStatus = (state) => state.lessonCycle.currentStatus;
export const selectCycleStreak = (state) => state.lessonCycle.current?.cycleStreak ?? 0;
export const selectTutorRoster = (state) => state.lessonCycle.roster;
export const selectTutorRosterStatus = (state) => state.lessonCycle.rosterStatus;
export default lessonCycleSlice.reducer;
